import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { authApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Lock, EnvelopeSimple, Globe } from '@phosphor-icons/react';
import { toast } from 'sonner';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotBusy, setForgotBusy] = useState(false);
  const { login } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();

  const submitForgot = async () => {
    if (!forgotEmail.includes('@')) {
      toast.error(language === 'fr' ? 'Email invalide' : 'Invalid email');
      return;
    }
    setForgotBusy(true);
    try {
      await authApi.forgotPassword(forgotEmail.trim().toLowerCase(), window.location.origin);
      toast.success(
        language === 'fr'
          ? 'Si ce compte existe, un email a été envoyé. Vérifiez votre boîte de réception.'
          : 'If the account exists, an email has been sent. Check your inbox.',
        { duration: 7000 }
      );
      setForgotOpen(false);
      setForgotEmail('');
    } catch {
      toast.error(language === 'fr' ? 'Erreur, réessayez plus tard' : 'Error, try again later');
    } finally {
      setForgotBusy(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Background image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{ 
          backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/568a569c-0fa6-40dc-9d3f-d310e1c010bc/images/4245737ad183253ed165d014c104ffa1ae93f54ac72925dcfa723608d34a2fdb.png)'
        }}
      >
        <div className="absolute inset-0 bg-[#0052FF]/70" />
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/568a569c-0fa6-40dc-9d3f-d310e1c010bc/images/8563b961e801bb242cecdbdafd9d4cf288a10e83064dc5a2edd376c57200f0da.png"
            alt="Logo"
            className="w-24 h-24 mb-8"
          />
          <h1 className="font-outfit text-4xl font-bold mb-4">
            {language === 'fr' ? 'Système de Gestion Scolaire' : 'School Management System'}
          </h1>
          <p className="font-manrope text-lg opacity-90">
            {language === 'fr' 
              ? 'Gérez efficacement votre établissement scolaire avec notre solution complète.'
              : 'Efficiently manage your school with our comprehensive solution.'}
          </p>
        </div>
      </div>
      
      {/* Right side - Login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center bg-[#F8FAFC] p-8">
        <div className="w-full max-w-md">
          {/* Language toggle */}
          <div className="flex justify-end mb-8">
            <Button
              variant="outline"
              size="sm"
              onClick={toggleLanguage}
              className="flex items-center gap-2 border-[#E2E8F0] hover:bg-[#F1F5F9]"
              data-testid="language-toggle"
            >
              <Globe size={18} />
              {language === 'fr' ? 'EN' : 'FR'}
            </Button>
          </div>
          
          <Card className="border border-[#E2E8F0] shadow-none">
            <CardHeader className="space-y-1">
              <div className="lg:hidden flex justify-center mb-4">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/568a569c-0fa6-40dc-9d3f-d310e1c010bc/images/8563b961e801bb242cecdbdafd9d4cf288a10e83064dc5a2edd376c57200f0da.png"
                  alt="Logo"
                  className="w-16 h-16"
                />
              </div>
              <CardTitle className="font-outfit text-2xl text-center text-[#0F172A]">
                {t('login')}
              </CardTitle>
              <CardDescription className="font-manrope text-center text-[#64748B]">
                {language === 'fr' 
                  ? 'Entrez vos identifiants pour accéder à votre compte'
                  : 'Enter your credentials to access your account'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm" data-testid="login-error">
                    {error}
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="email" className="font-manrope text-[#0F172A]">{t('email')}</Label>
                  <div className="relative">
                    <EnvelopeSimple className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" size={20} />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="admin@school.com"
                      className="pl-10 border-[#E2E8F0] focus:border-[#0052FF] focus:ring-[#0052FF]"
                      required
                      data-testid="email-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password" className="font-manrope text-[#0F172A]">{t('password')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" size={20} />
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="pl-10 border-[#E2E8F0] focus:border-[#0052FF] focus:ring-[#0052FF]"
                      required
                      data-testid="password-input"
                    />
                  </div>
                </div>
                
                <Button
                  type="submit"
                  className="w-full bg-[#0052FF] hover:bg-[#0043D1] text-white font-manrope"
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading ? t('loading') : t('loginButton')}
                </Button>
              </form>
              
              <div className="mt-4 text-center space-y-2">
                <button
                  type="button"
                  onClick={() => { setForgotEmail(email); setForgotOpen(true); }}
                  className="text-sm text-[#0052FF] hover:underline font-manrope"
                  data-testid="forgot-password-link"
                >
                  {language === 'fr' ? 'Mot de passe oublié ?' : 'Forgot password?'}
                </button>
                <p className="text-sm text-[#64748B] font-manrope">
                  {language === 'fr' ? 'Vous êtes parent ?' : 'Are you a parent?'}{' '}
                  <a href="/parent/register" className="text-[#10B981] hover:underline font-medium">
                    {language === 'fr' ? 'Créer un compte parent' : 'Create parent account'}
                  </a>
                </p>
                <p className="text-sm text-[#64748B] font-manrope pt-2 border-t border-[#E2E8F0]">
                  {language === 'fr' ? 'Enseignant ou personnel ?' : 'Teacher or staff?'}{' '}
                  <a href="/m/install" className="text-[#0052FF] hover:underline font-medium" data-testid="link-mobile-install">
                    {language === 'fr' ? 'Installer l\'app mobile' : 'Install the mobile app'}
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>
          
          <p className="mt-6 text-center text-sm text-[#64748B] font-manrope">
            &copy; 2024 School Management System
          </p>
        </div>
      </div>

      {/* Forgot password dialog */}
      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="max-w-md" data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit">
              {language === 'fr' ? 'Mot de passe oublié' : 'Forgot password'}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr'
                ? 'Entrez votre email. Si un compte existe, un lien de réinitialisation valide 1 heure vous sera envoyé.'
                : 'Enter your email. If an account exists, a reset link valid for 1 hour will be sent.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="font-manrope">{language === 'fr' ? 'Email du compte' : 'Account email'}</Label>
            <Input
              type="email"
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              placeholder="vous@exemple.com"
              data-testid="forgot-email-input"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setForgotOpen(false)}>{language === 'fr' ? 'Annuler' : 'Cancel'}</Button>
            <Button
              onClick={submitForgot}
              disabled={forgotBusy}
              className="bg-[#0052FF] hover:bg-[#0040CC]"
              data-testid="forgot-submit-btn"
            >
              {forgotBusy
                ? (language === 'fr' ? 'Envoi…' : 'Sending…')
                : (language === 'fr' ? 'Envoyer le lien' : 'Send link')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
